import { http } from "./client";

// Asset management: asset types, assets, and assignments (custody records).
// Every function returns the response body.
const data = (r) => r.data;

/** Drop empty filters so they aren't sent as `status=` etc. */
const clean = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, v]) => v !== undefined && v !== null && v !== "" && v !== false,
    ),
  );

/** Empty strings in a form become null so optional fields can be cleared. */
const nullIfEmpty = (body) =>
  Object.fromEntries(
    Object.entries(body).map(([k, v]) => [k, v === "" ? null : v]),
  );

/* ---------- Asset types (Admin/HR) ---------- */
export const assetTypeApi = {
  // GET /asset-types?includeInactive=true → AssetType[] (sorted by name)
  getAll: (includeInactive = false) =>
    http
      .get("/asset-types", {
        params: includeInactive ? { includeInactive: true } : {},
      })
      .then(data),
  create: (body) => http.post("/asset-types", nullIfEmpty(body)).then(data),
  update: (id, body) =>
    http.patch(`/asset-types/${id}`, nullIfEmpty(body)).then(data),
  remove: (id) => http.delete(`/asset-types/${id}`).then(() => id),
};

/* ---------- Assets ---------- */
export const assetApi = {
  /**
   * GET /assets → { data, total, page, limit } (newest first)
   * filters: status, assetTypeId, branchId, companyId, currentEmployeeId, search,
   *          warrantyExpiringBefore (YYYY-MM-DD), includeDeleted, page, limit (max 100)
   */
  getList: (filters = {}) =>
    http.get("/assets", { params: clean(filters) }).then(data),
  // GET /assets/:id → Asset & { currentAssignment }
  getById: (id) => http.get(`/assets/${id}`).then(data),
  // GET /assets/:id/history → AssetAssignment[] (newest first)
  getHistory: (id) => http.get(`/assets/${id}/history`).then(data),
  create: (body) => http.post("/assets", nullIfEmpty(body)).then(data),
  // Company can't change; branch and status have their own endpoints
  update: (id, body) =>
    http.patch(`/assets/${id}`, nullIfEmpty(body)).then(data),
  // status: Available | Maintenance | Retired | Lost
  setStatus: (id, { status, remarks }) =>
    http.patch(`/assets/${id}/status`, clean({ status, remarks })).then(data),
  // branchId: null = company level
  moveBranch: (id, branchId) =>
    http
      .patch(`/assets/${id}/branch`, { branchId: branchId || null })
      .then(data),
  // Soft delete; history is kept
  remove: (id) => http.delete(`/assets/${id}`).then(() => id),
};

/* ---------- Assignments ---------- */
export const assignmentApi = {
  // POST /asset-assignments → AssetAssignment (asset becomes Assigned)
  // dateReceived: YYYY-MM-DD (default today on the server)
  assign: ({
    assetId,
    employeeId,
    expectedReturnDate,
    condition,
    notes,
    dateReceived,
  }) =>
    http
      .post(
        "/asset-assignments",
        clean({
          assetId,
          employeeId,
          expectedReturnDate,
          condition,
          notes,
          dateReceived,
        }),
      )
      .then(data),
  // PATCH /asset-assignments/:id/date-received { dateReceived }
  setDateReceived: (id, dateReceived) =>
    http
      .patch(`/asset-assignments/${id}/date-received`, { dateReceived })
      .then(data),
  // PATCH /asset-assignments/:id/return  { condition, nextStatus?, notes? }
  returnAsset: (id, { condition, nextStatus, notes }) =>
    http
      .patch(
        `/asset-assignments/${id}/return`,
        clean({ condition, nextStatus, notes }),
      )
      .then(data),
  // POST /asset-assignments/:id/transfer → the NEW assignment
  transfer: (id, { employeeId, expectedReturnDate, notes }) =>
    http
      .post(`/asset-assignments/${id}/transfer`, {
        employeeId,
        expectedReturnDate: expectedReturnDate || null,
        ...clean({ notes }),
      })
      .then(data),
  // PATCH /asset-assignments/:id/lost  { notes } (required)
  markLost: (id, notes) =>
    http.patch(`/asset-assignments/${id}/lost`, { notes }).then(data),

  /**
   * GET /asset-assignments → { data, total, page, limit }
   * filters: status, employeeId, assetId, branchId, pendingAcknowledgement, overdue, search, page, limit
   */
  getList: (filters = {}) =>
    http.get("/asset-assignments", { params: clean(filters) }).then(data),
  getById: (id) => http.get(`/asset-assignments/${id}`).then(data),
  // GET /asset-assignments/employee/:employeeId → { employee, clearance, active, history }
  getClearance: (employeeId) =>
    http.get(`/asset-assignments/employee/${employeeId}`).then(data),
};

/**
 * Documents attached to an asset (receipt, warranty, accountability form, photos).
 * `url` is a storage share link (Zoho WorkDrive or S3), opened in a new tab.
 */
export const assetDocumentApi = {
  // → [{ id, category, fileName, mimeType, size, url, provider, remarks, uploadedBy, createdAt }]
  list: (assetId) => http.get(`/assets/${assetId}/documents`).then(data),
  // multipart: category (RECEIPT | WARRANTY | ACCOUNTABILITY_FORM | PHOTO | OTHER), remarks, then file
  upload: (assetId, { category, remarks, file }) => {
    const form = new FormData();
    form.append("category", category);
    if (remarks) form.append("remarks", remarks);
    form.append("file", file);
    return http.post(`/assets/${assetId}/documents`, form).then(data);
  },
  remove: (assetId, documentId) =>
    http
      .delete(`/assets/${assetId}/documents/${documentId}`)
      .then(() => documentId),
};
